import React, { useMemo, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { faDownload } from '@fortawesome/pro-light-svg-icons';
import { useAsyncStateWithIsLoading, useQueryState, useDefaultScrollToId, useParamReplace } from '@/utils/hooks';
import { searchOrders, OrderSearchOptions } from '@/services/orders';
import OrderList from '@/seller/components/orders/OrderList';
import { OrderType, OrderStatus } from '@/types';
import { LoadingIndicator, useIsFilterModified } from '@ygtechnologies/bygr-frontend-component-library';
import Pagination, { usePagination, paginateArray } from '@/components/Pagination';
import OrderFilters, { OrdersFilterOptions } from './OrderFilters';
import { Button, ButtonBar, SlideToggle, Tabs } from '@ygtechnologies/rubus-ui';
import { Link } from 'react-router-dom';
import { OrderExportModal } from './OrderExport/OrderExportModal';
import { OrderSortingDropdown } from './OrderSortingDropdown';
import { useOrderSorting } from './hooks';

export type OrdersViewMode = 'all' | 'change-requests' | 'options';

interface OrdersFilterableListProps {
    projectId: number;
    mode: OrdersViewMode;
}

function getOrderTypes(mode: OrdersViewMode) {
    switch (mode) {
        case 'change-requests':
            return [OrderType.ChangeRequest];
        case 'options':
            return [OrderType.Option, OrderType.Preset];
        default:
            return [];
    }
}

const OrdersFilterableList: React.FC<OrdersFilterableListProps> = ({ projectId, mode }) => {
    const { sortDefinitions, currentSort, setCurrentSort, currentSortDefinition, applySorting } = useOrderSorting();

    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [filter, setFilter] = useQueryState(OrdersFilterOptions.empty());
    const [showCancelled, setShowCancelled] = useState(false);
    const defaultExpandedId = useDefaultScrollToId();
    const filterParameters = useMemo(
        () =>
            ({
                projectId,
                status: showCancelled ? undefined : [OrderStatus.ReadyToStart],
                residentialUnitIds: filter.units,
                ownerProfileIds: filter.owners,
                orderTypes: getOrderTypes(mode),
                categoryIds: filter.itemCategories,
                searchText: filter.searchText,
                tagIds: filter.tags,
                residentialUnitGroupIds: filter.residentialUnitGroupIds,
                projectDeadlineIds: filter.projectDeadlineIds,
                defaultBuyerOptions: filter.defaultBuyerOptions === 'HideDefault' ? false : undefined,
                orderDateFrom: filter.orderDateFrom,
                orderDateTo: filter.orderDateTo,
            }) as OrderSearchOptions,
        [projectId, mode, filter, showCancelled]
    );

    const [orders, setOrders, , isLoading] = useAsyncStateWithIsLoading(
        () => searchOrders(filterParameters),
        [filterParameters],
        null
    );

    const sortedOrders = useMemo(() => {
        if (!orders) return orders;
        return applySorting(orders);
    }, [orders, applySorting]);

    const pagination = usePagination(sortedOrders?.length);
    const filterModified = useIsFilterModified(filter, OrdersFilterOptions.empty);

    const handleFilterChange = (newValue: OrdersFilterOptions) => {
        setFilter(newValue);
        pagination.setPage(0);
    };

    const replaceParams = useParamReplace<{ mode?: string }>();

    return (
        <div>
            <ButtonBar sticky margins="none" stickyClassName="stickyBleed">
                <Tabs activeId={mode} variant="pill" size="sm">
                    <Tabs.Tab
                        id="all"
                        as={Link}
                        to={replaceParams({ mode: undefined })}
                        label={<Trans>All</Trans>}
                    />
                    <Tabs.Tab
                        id="change-requests"
                        as={Link}
                        to={replaceParams({ mode: 'change-requests' })}
                        label={<Trans>Change request orders</Trans>}
                    />
                    <Tabs.Tab
                        id="options"
                        as={Link}
                        to={replaceParams({ mode: 'options' })}
                        label={<Trans>Option orders</Trans>}
                    />
                </Tabs>

                <ButtonBar.Right>
                    {mode === 'options' && (
                        <SlideToggle
                            onChange={(e) =>
                                setFilter({ ...filter, defaultBuyerOptions: e.target.checked ? 'HideDefault' : 'All' })
                            }
                            checked={filter.defaultBuyerOptions === 'HideDefault'}>
                            <Trans>Hide default options</Trans>
                        </SlideToggle>
                    )}

                    <OrderSortingDropdown
                        currentSort={currentSort}
                        sortDefinitions={sortDefinitions}
                        setCurrentSort={setCurrentSort}
                        currentSortDefinition={currentSortDefinition}
                    />

                    <SlideToggle onChange={() => setShowCancelled(!showCancelled)} checked={showCancelled}>
                        <Trans>Show cancelled orders</Trans>
                    </SlideToggle>

                    <Button
                        variant="tertiary"
                        disabled={orders?.length === 0}
                        icon={faDownload}
                        onClick={(e) => setExportModalOpen(true)}>
                        <Trans>Export</Trans>
                    </Button>

                    <OrderFilters
                        projectId={projectId}
                        onClear={() => handleFilterChange(OrdersFilterOptions.empty())}
                        onFilterChange={handleFilterChange}
                        filter={filter}
                        isLoading={isLoading}
                        disableStatus={true}
                    />
                </ButtonBar.Right>
            </ButtonBar>

            <OrderExportModal
                isOpen={exportModalOpen}
                onClose={() => setExportModalOpen(false)}
                filter={filterParameters}
                showFilterWarning={filterModified || mode !== 'all'}
            />

            {orders ? (
                <>
                    <OrderList
                        defaultExpandedId={defaultExpandedId}
                        orders={paginateArray(pagination, sortedOrders || [])}
                        onChange={(changes) => setOrders(changes.updateArray(orders))}
                    />

                    <Pagination value={pagination} />
                </>
            ) : (
                <LoadingIndicator />
            )}
        </div>
    );
};

export default OrdersFilterableList;
